import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useAbility } from '@/hooks/ability';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader } from 'lucide-react';

const buttonVariants = cva(
  'group/button gap-2 inline-flex relative items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors outline-none focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed border border-transparent transition-all focus:border-primary/40 focus:shadow-[0px_0px_3px_3px_#ffa13c1A]',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-white hover:text-white hover:bg-primary/90 focus:bg-white focus:text-primary focus:border-primary focus-within:bg-white focus-within:text-primary focus-within:border-primary',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:bg-destructive/50',
        success: 'bg-green-400 text-white hover:bg-green-500 focus:bg-green-600',
        outline: 'border border-gray-200 bg-background hover:bg-accent hover:text-accent-foreground focus:bg-accent',
        secondary: 'bg-secondary text-primary hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        underline:
          'bg-transparent border-x-0 border-t-0 rounded-none border-b-[3px] py-0 text-primary border-black focus:border-x-0 focus:border-t-0',
      },
      size: {
        default: 'px-4 h-8',
        sm: 'rounded-md px-3 h-8',
        lg: ' rounded-md px-8',
        icon: 'p-1',
      },
      roundness: {
        default: '',
        rounded: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  permissions?: string | string[];
  isLoading?: boolean;
  hideOnUnauthorized?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, disabled, permissions, isLoading, hideOnUnauthorized, ...props },
    ref,
  ) => {
    const ability = useAbility();
    const { t } = useTranslation();
    const hasPermission =
      !permissions ||
      (Array.isArray(permissions)
        ? permissions.every(permission => ability.can(permission, ''))
        : ability.can(permissions, ''));
    const Comp = (asChild ? Slot : 'button') as any;

    if (hideOnUnauthorized && !hasPermission) return null;

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            className: cn('relative', className, isLoading && 'opacity-10', !hasPermission ? 'cursor-not-allowed' : ''),
          }),
        )}
        ref={ref}
        {...props}
        title={!hasPermission ? t('no-permission') : props.title}
        disabled={!hasPermission || disabled || isLoading}
      >
        {props.children}
        {isLoading ? (
          <span className={cn('absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-blue-700')}>
            <Loader className="animate-spin" />
          </span>
        ) : null}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export interface FlatButtonProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

const FlatButton = React.forwardRef<HTMLDivElement, FlatButtonProps>(
  ({ className, variant, size, isLoading, roundness, ...props }, ref) => {
    return (
      <div
        className={cn(buttonVariants({ variant, size, className, roundness }), isLoading && 'opacity-30')}
        ref={ref}
        {...props}
        onClick={!isLoading ? props.onClick : undefined}
      />
    );
  },
);
FlatButton.displayName = 'FlatButton';

export { Button, buttonVariants, FlatButton };
